import { ClientPlugin, RequestConfig, CachePluginConfig } from "../types";

const DEFAULT_CACHE_CONFIG: Required<CachePluginConfig> = {
  enabled: true,
  ttl: 300,
  exclude: [],
  maxSize: 100,
  invalidateOnMutation: true,
};

export class CachePlugin implements ClientPlugin {
  name = "cache";
  private cache = new Map<string, { data: any; expires: number }>();
  private config: Required<CachePluginConfig>;

  constructor(config: CachePluginConfig = {}) {
    this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
  }

  beforeRequest(config: RequestConfig) {
    if (!this.config.enabled) return config;

    const cacheKey = this.getCacheKey(config);
    const cached = this.cache.get(cacheKey);

    if (cached && cached.expires > Date.now()) {
      throw { isCachedResponse: true, data: cached.data };
    }

    return config;
  }

  afterRequest(response: any, config: RequestConfig) {
    if (!this.config.enabled) return response;

    if (this.shouldCache(config)) {
      const cacheKey = this.getCacheKey(config);
      this.cache.set(cacheKey, {
        data: response,
        expires: Date.now() + this.config.ttl * 1000,
      });

      // Cleanup if cache is too large
      if (this.cache.size > this.config.maxSize) {
        const oldestKey = this.cache.keys().next().value;

        if (oldestKey) {
          this.cache.delete(oldestKey);
        }
      }
    }
    return response;
  }

  private getCacheKey(config: RequestConfig): string {
    return JSON.stringify({
      query: config.operation.document,
      variables: config.variables,
    });
  }

  private shouldCache(config: RequestConfig): boolean {
    if (this.config.exclude.includes(config.operation.key || "")) {
      return false;
    }

    // Don't cache mutations if invalidateOnMutation is true
    if (
      this.config.invalidateOnMutation &&
      config.operation.document.kind.trim().startsWith("mutation")
    ) {
      return false;
    }

    return true;
  }
}
